import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../../core/auth/auth.service';

/**
 * Personal greeting for the landing page (EB-3621 Plan 5) — de-spiked from
 * `spike-welcome-bar`. Shows today's date and a "Welcome, {firstName}." headline.
 * `align` controls horizontal alignment ('start' for the classic editorial landing,
 * 'center' for the unified landing hero).
 */
@Component({
  selector: 'cmt-welcome-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="flex flex-col gap-2 cmt-reveal"
      [class.items-start]="align() === 'start'"
      [class.items-center]="align() === 'center'"
      [class.text-center]="align() === 'center'"
      style="animation-delay:80ms"
    >
      <span class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
        {{ todayFormatted() }}
      </span>
      <h1 class="font-display text-[clamp(2.25rem,4.5vw,3.5rem)] font-medium leading-[1.05] text-foreground">
        <span class="italic text-muted-foreground/80">Welcome,</span>
        <span class="ml-1 text-foreground">{{ firstName() }}</span>
        <span class="text-accent">.</span>
      </h1>
    </div>
  `,
})
export class WelcomeBarComponent {
  readonly align = input<'start' | 'center'>('start');

  private readonly authService = inject(AuthService);

  private readonly user = toSignal(this.authService.user$);
  protected readonly firstName = computed(() => this.user()?.firstname ?? '');

  protected readonly todayFormatted = computed(() =>
    new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
  );
}
